import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, XCircle, FileText, Buildings, PencilSimple } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Phase 207h / 208 — Public Approval Page (no auth required).
 *
 * Linked from the email sent to managers. Shows meeting metadata + embedded
 * PDF preview + Approve / Reject / Request-changes buttons.
 *
 * If the email link includes `?action=approve|reject|request_changes`, the
 * page pre-selects that action and asks the manager to confirm (no auto-fire,
 * to avoid accidental clicks from preview crawlers).
 */
export default function PublicApproval() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const presetAction = searchParams.get('action'); // approve | reject | request_changes
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [decision, setDecision] = useState(null); // 'approve' | 'reject' | 'request_changes' | null
  const [note, setNote] = useState('');
  const [pendingAction, setPendingAction] = useState(null); // confirmation step

  useEffect(() => {
    axios
      .get(`${API}/public/approval/${token}`)
      .then((r) => setData(r.data))
      .catch((err) => setData({ valid: false, message: err?.response?.data?.detail || 'Σφάλμα' }))
      .finally(() => setLoading(false));
  }, [token]);

  // Auto-open confirmation panel when arriving via an action link
  useEffect(() => {
    if (presetAction && ['approve', 'reject', 'request_changes'].includes(presetAction)) {
      setPendingAction(presetAction);
    }
  }, [presetAction]);

  const submit = async (action) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/approval/${token}/decision`, {
        action,
        note: action !== 'approve' ? note : '',
      });
      setDecision(action);
      const messages = {
        approve: 'Εγκρίθηκε',
        reject: 'Απορρίφθηκε',
        request_changes: 'Στάλθηκε αίτημα αλλαγών',
      };
      toast.success(messages[action]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Σφάλμα');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <p className="text-zinc-400 text-sm">Φόρτωση…</p>
      </div>
    );
  }

  if (!data || !data.valid) {
    // Phase 735.12 — Distinguish "already answered" from "invalid link".
    const alreadyAnswered = data && data.manager_status && data.manager_status !== 'pending';
    const statusLabels = {
      approved: 'Έχετε ήδη εγκρίνει.',
      rejected: 'Έχετε ήδη απορρίψει.',
      changes_requested: 'Έχετε ήδη ζητήσει αλλαγές.',
    };
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
        <div className="bg-white border border-zinc-200 max-w-md w-full p-8 text-center" data-testid="approval-invalid">
          {alreadyAnswered ? (
            <>
              <CheckCircle size={48} weight="fill" className="text-emerald-500 mx-auto mb-3" />
              <h1 className="text-lg font-bold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                Έχει καταχωρηθεί
              </h1>
              <p className="text-sm text-zinc-500">
                {statusLabels[data.manager_status] || 'Έχετε ήδη απαντήσει σε αυτό το αίτημα έγκρισης.'}
              </p>
              {data.manager_name && (
                <p className="text-xs text-zinc-400 mt-3">Παραλήπτης: {data.manager_name}</p>
              )}
            </>
          ) : (
            <>
              <XCircle size={48} weight="fill" className="text-red-500 mx-auto mb-3" />
              <h1 className="text-lg font-bold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                Σύνδεσμος μη έγκυρος
              </h1>
              <p className="text-sm text-zinc-500">{data?.message || 'Το link δεν είναι πλέον διαθέσιμο.'}</p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (decision === 'approve') {
    return <ResultCard testId="approval-success" icon={CheckCircle} color="emerald" title="Ευχαριστούμε!" message="Η έγκρισή σας καταχωρήθηκε επιτυχώς." />;
  }
  if (decision === 'reject') {
    return <ResultCard testId="reject-success" icon={XCircle} color="red" title="Απορρίφθηκε" message="Η απόρριψή σας καταχωρήθηκε. Ο διαχειριστής θα ενημερωθεί." />;
  }
  if (decision === 'request_changes') {
    return <ResultCard testId="changes-success" icon={PencilSimple} color="amber" title="Αίτημα αλλαγών στάλθηκε" message="Ο διαχειριστής της εφαρμογής θα ενημερωθεί για τις αλλαγές που ζητήσατε." />;
  }

  const ACTION_META = {
    approve: { label: 'Έγκριση', color: 'emerald', desc: 'Επιβεβαιώνετε ότι τα πρακτικά είναι σωστά.' },
    reject: { label: 'Απόρριψη', color: 'red', desc: 'Δηλώνετε ότι τα πρακτικά πρέπει να απορριφθούν.' },
    request_changes: { label: 'Αίτηση Αλλαγών', color: 'amber', desc: 'Ζητάτε αλλαγές πριν την έγκριση.' },
  };

  return (
    <div className="min-h-screen bg-zinc-50 py-8 px-4" data-testid="public-approval-page">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white border border-zinc-200 mb-4">
          <div className="px-6 py-5 border-b border-zinc-100">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-2">
              <span className="inline-block w-2 h-2 rounded-full bg-amber-500"></span>
              ΠΡΟΣ ΕΓΚΡΙΣΗ
            </div>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
              Πρακτικά Συνέλευσης
            </h1>
            <p className="text-sm text-zinc-500 mt-1">Καλείστε ως διαχειριστής να εγκρίνετε, να απορρίψετε ή να ζητήσετε αλλαγές.</p>
          </div>
          <div className="px-6 py-4 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Field label="Πολυκατοικία" value={data.building_name} icon={Buildings} />
            <Field label="Διαχειριστής" value={data.manager_name} />
            <Field label="Ημερομηνία" value={data.meeting_date} />
            <Field label="Ώρα" value={data.meeting_time || '—'} />
            <Field label="Τύπος" value={(data.meeting_type || '').toUpperCase()} />
            <Field label="Θέματα" value={(data.topics || []).length || '—'} />
          </div>
          {data.approvals && data.approvals.length > 1 && (
            <div className="px-6 py-3 border-t border-zinc-100 bg-zinc-50/40">
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mb-1.5">Όλοι οι διαχειριστές</p>
              <div className="flex flex-wrap gap-2">
                {data.approvals.map((a) => {
                  const cls =
                    a.status === 'approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                    a.status === 'rejected' ? 'bg-red-50 text-red-700 border-red-200' :
                    a.status === 'changes_requested' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                    'bg-zinc-50 text-zinc-500 border-zinc-200';
                  const icon =
                    a.status === 'approved' ? '✓ ' :
                    a.status === 'rejected' ? '✗ ' :
                    a.status === 'changes_requested' ? '✎ ' :
                    '⏳ ';
                  return (
                    <span key={a.name} className={`text-[11px] px-2 py-0.5 border ${cls}`}>
                      {icon}{a.name}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* PDF preview */}
        <div className="bg-white border border-zinc-200 mb-4 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-zinc-100 flex items-center gap-2">
            <FileText size={14} className="text-zinc-500" />
            <h2 className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Πρακτικά (PDF)</h2>
            <a
              href={`${API}/public/approval/${token}/pdf`}
              target="_blank"
              rel="noreferrer"
              className="ml-auto text-[11px] text-blue-600 hover:underline"
              data-testid="open-pdf-new-tab"
            >
              Άνοιγμα σε νέο tab
            </a>
          </div>
          <iframe
            title="Meeting PDF"
            src={`${API}/public/approval/${token}/pdf`}
            className="w-full"
            style={{ height: '70vh', border: 0 }}
            data-testid="approval-pdf-iframe"
          />
        </div>

        {/* Action panel */}
        <div className="bg-white border border-zinc-200 px-6 py-5">
          <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
            Ενέργεια
          </h3>

          {!pendingAction ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setPendingAction('approve')}
                disabled={submitting}
                className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold uppercase tracking-wider disabled:opacity-50"
                data-testid="approve-btn"
              >
                ✓ Έγκριση
              </button>
              <button
                type="button"
                onClick={() => setPendingAction('reject')}
                disabled={submitting}
                className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold uppercase tracking-wider disabled:opacity-50"
                data-testid="reject-btn"
              >
                ✗ Απόρριψη
              </button>
              <button
                type="button"
                onClick={() => setPendingAction('request_changes')}
                disabled={submitting}
                className="px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold uppercase tracking-wider disabled:opacity-50"
                data-testid="request-changes-btn"
              >
                ✎ Αλλαγές
              </button>
            </div>
          ) : (
            <ConfirmAction
              action={pendingAction}
              meta={ACTION_META[pendingAction]}
              note={note}
              setNote={setNote}
              submitting={submitting}
              onCancel={() => { setPendingAction(null); setNote(''); }}
              onConfirm={() => submit(pendingAction)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ConfirmAction({ action, meta, note, setNote, submitting, onCancel, onConfirm }) {
  const colorClasses = {
    emerald: 'bg-emerald-600 hover:bg-emerald-700',
    red: 'bg-red-600 hover:bg-red-700',
    amber: 'bg-amber-600 hover:bg-amber-700',
  };
  return (
    <div className="space-y-3" data-testid={`confirm-${action}`}>
      <div className="px-3 py-2 bg-zinc-50 border-l-4 border-zinc-300 text-xs text-zinc-600">
        <strong className="block uppercase tracking-wider text-[10px] mb-0.5">Επιβεβαίωση: {meta.label}</strong>
        {meta.desc}
      </div>
      {action !== 'approve' && (
        <>
          <p className="text-xs text-zinc-500">Σημείωση {action === 'reject' ? '(λόγος απόρριψης)' : '(τι θέλετε να αλλάξει)'}{action === 'reject' ? ':' : ' (προαιρετικό):'}</p>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            className="w-full border border-zinc-300 px-3 py-2 text-sm"
            placeholder={action === 'reject' ? 'π.χ. Λάθος στοιχεία, ασυμφωνία με τα παρόντα μέλη…' : 'π.χ. Λάθος ώρα, να διορθωθεί το θέμα 2…'}
            data-testid={`${action}-note`}
          />
        </>
      )}
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="px-3 py-1.5 border border-zinc-300 text-xs hover:bg-zinc-50"
          data-testid={`cancel-${action}-btn`}
        >
          Άκυρο
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={submitting}
          className={`px-4 py-1.5 ${colorClasses[meta.color]} text-white text-xs font-semibold uppercase tracking-wider disabled:opacity-50`}
          data-testid={`confirm-${action}-btn`}
        >
          {submitting ? '…' : `Επιβεβαίωση ${meta.label}`}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{label}</p>
      <p className="text-sm text-zinc-800 font-medium">{value || '—'}</p>
    </div>
  );
}

function ResultCard({ testId, icon: Icon, color, title, message }) {
  const colorMap = {
    emerald: 'text-emerald-500',
    amber: 'text-amber-500',
    red: 'text-red-500',
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
      <div className="bg-white border border-zinc-200 max-w-md w-full p-10 text-center" data-testid={testId}>
        <Icon size={56} weight="fill" className={`${colorMap[color]} mx-auto mb-4`} />
        <h1 className="text-xl font-bold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
          {title}
        </h1>
        <p className="text-sm text-zinc-500">{message}</p>
      </div>
    </div>
  );
}
